import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ApplicationDTO } from '../../../../../models/application/ApplicationDTO';
import { ApplicationBankDetailsDTO } from '../../../../../models/application/ApplicationBankDetailsDTO';
import {MatDialog} from '@angular/material/dialog';
import { SharedService } from 'src/app/modules/common/services/shared-service';
import { OnboardingAdminPortalService } from 'src/app/modules/onboarding-admin-portal/services/onboard-application.service';
import { UserAuthorisationService } from 'src/app/modules/common/services/user-authorisation.service';
import { ApplicationBankDetailDialogComponent } from '../../../../dialogs/application-bank-detail-update-dialog/application-bank-detail-update-dialog';
import { finalize, take } from 'rxjs';
import { DisplayStrings } from 'src/app/modules/common/constants/DisplayStrings';
import { ApplicationUtilityService } from 'src/app/modules/onboarding-admin-portal/services/application-utility.service';
import {getStatusColor} from '../../../../../../common/util/Util';

enum TableColumns {
  Usage = 'usage',
  BankCode = 'bankCode',
  BankAccountType = 'bankAccountType',
  BranchCode = 'branchCode',
  AccountNumber = 'accountNumber',
  AccountName = 'accountName',
  DebitOrderDay = 'debitOrderDay',
  IsThirdPartyAccount = 'isThirdPartyAccount',
  IsVerified = 'isVerified',
  Actions = 'actions'
}

@Component({
  selector: 'greydom-application-bank-details',
  templateUrl: './application-bank-details.component.html',
  styleUrls: []
})
export class ApplicationBankDetailsComponent implements OnChanges {
  readonly getStatusColor = getStatusColor;

  readonly tableColumns = TableColumns;
  readonly DisplayStrings = DisplayStrings;

  @Input() application: ApplicationDTO;
  @Input() userFirstLastName = '';
  @Output() bankDetailsUpdated = new EventEmitter<ApplicationBankDetailsDTO[]>();
  bankDetails: ApplicationBankDetailsDTO[];
  displayedColumns: string[] = Object.values(TableColumns);
  isProcessing = false;
  hasPermissions: boolean;
  isApplicationLocked = false;

  constructor(
    private dialog: MatDialog,
    private sharedService: SharedService,
    private applicationService: OnboardingAdminPortalService,
    private userAuthorisationService: UserAuthorisationService,
    private applicationUtilityService: ApplicationUtilityService
   ) {
     this.hasPermissions = this.userAuthorisationService.hasOnBoardingQC() || this.userAuthorisationService.hasRoleEnterpriseManager();
   }

  ngOnInit() {
    this.hasPermissions = this.userAuthorisationService.hasOnBoardingQC() || this.userAuthorisationService.hasRoleEnterpriseManager();
    this.isApplicationLocked = this.applicationUtilityService.isApplicationLocked(this.application?.status?.applicationStatusId, this.application?.stage?.applicationStageId);
  }

  get isDataExist(): boolean {
    return !!(this.bankDetails && this.bankDetails.length);
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes.application && changes.application.currentValue) {
      this.bankDetails = this.application.membership?.bankDetails || [];
    }
  }

  getVerificationStatus(status: number): string {
    switch (status) {
      case -1: return 'No';
      case 0: return 'Unknown';
      case 1: return 'Yes';
      default: return 'Unknown';
    }
  }

  onEditBankDetails(bankAccountDetails: ApplicationBankDetailsDTO): void {
    if (!this.hasPermissions) {
      return;
    }

    const dialogRef = this.dialog.open(ApplicationBankDetailDialogComponent, {
      width: '40vw',
      data: {bankAccountDetails}
    });

    dialogRef.afterClosed().subscribe((updatedBankAccountDetails: ApplicationBankDetailsDTO) => {
      if (updatedBankAccountDetails) {
        const index = this.bankDetails.findIndex(bd => bd.applicationBankDetailsId === bankAccountDetails.applicationBankDetailsId);
        if (index !== -1) {
          this.bankDetails[index] = updatedBankAccountDetails;
          this.updateApplication();
        }
      }
    });
  }

  editTooltip(): string {
    const permissionMessage = DisplayStrings.permissionApplicationMessages?.rolePermissionUpdate;
    const statusMessage = DisplayStrings.permissionApplicationMessages?.statusUpdate;
    const editMessage = 'Edit Bank Details';
    if (!this.hasPermissions) {
      return permissionMessage;
    } else if (this.isApplicationLocked) {
      return statusMessage;
    } else {
      return editMessage;
    }
  }

  private updateApplication(): void {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    // Update Audit fields
    this.application.updated = new Date();
    this.application.updatedBy = this.userFirstLastName;
    this.applicationService.updateApplication(this.application)
      .pipe(
        take(1),
        finalize(() => this.isProcessing = false)
      )
      .subscribe({
        next: (response: ApplicationDTO) => {
          if (response) {
            this.application = response;
            this.bankDetails = response.membership?.bankDetails;
            this.bankDetailsUpdated.emit(this.bankDetails);
            this.sharedService.openSnackBar('Banking details updated successfully', 'Dismiss');
          }
        },
        error: (error) => {
          console.error('Error updating application', error);
          this.sharedService.openErrorDialog(error.message);
        }
      });
  }

}
